import React, { useMemo, useState } from 'react';
import { Button, Tab, Tabs } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DataEmptyMessage from '@components/common/DataEmptyMessage';
import DataLoadingError from '@components/common/DataLoadingError';
import OverlaySpinner from '@components/common/OverlaySpinner';
import useEventListQuery from '@components/common/hooks/api/useEventListQuery';
import EventFilters from '@components/ContentComponents/Events/EventFilters';
import EventsTable from './EventsTable';

const ViewEvents = () => {
  const [currentTab, setCurrentTab] = useState('community');
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const filters = {
    project: searchParams.getAll('project'),
    main_office_id: searchParams.getAll('main_office_id'),
    created_by: searchParams.getAll('created_by'),
  };

  const filtersApplied = Object.values(filters).some(v => !!v);

  const {
    data: events,
    isLoading,
    isRefetching,
    error,
    isError,
  } = useEventListQuery({ filters });

  const { communityEvents, testEvents } = useMemo(() => {
    if (events && events.length) {
      return {
        communityEvents: events.filter((e) => !e.is_test),
        testEvents: events.filter((e) => e.is_test),
      };
    } else {
      return {
        communityEvents: [],
        testEvents: [],
      };
    }
  }, [events]);

  return (
    <div id="events" style={{ position: 'relative' }}>
      <OverlaySpinner condition={isRefetching} />
      <Grid container spacing={1}>
        <Grid item xs={12} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            startIcon={
              <Add sx={{ margin: "-5px", fontSize: '24px !important' }}/>
            }
            onClick={() => {
              navigate('/events/create');
            }}
          >
            Create Event
          </Button>
        </Grid>

        <EventFilters />

        <OverlaySpinner condition={isLoading} fixed />

        {!isLoading && (
          <>
            {!isError && events && events.length > 0 && (
              <Grid item xs={12}>
                <Tabs
                  value={currentTab}
                  onChange={(event, newValue) => setCurrentTab(newValue)}
                >
                  <Tab
                    label="Community Events"
                    value="community"
                    data-cy="tab-community"
                    aria-selected={currentTab === 'community' ? 'true' : 'false'}
                    id="community-events-tab"
                    aria-controls="community-events-panel"
                  />
                  <Tab
                    label="Test Events"
                    value="test"
                    data-cy="tab-test"
                    aria-selected={currentTab === 'test' ? 'true' : 'false'}
                    id="test-events-tab"
                    aria-controls="test-events-panel"
                  />
                </Tabs>
                {currentTab === 'community' && (
                  <div
                    role="tabpanel"
                    id="community-events-panel"
                    aria-labelledby="community-events-tab"
                    aria-hidden={currentTab !== 'community' ? 'true' : 'false'}
                  >
                    {communityEvents.length ? (
                      <EventsTable events={communityEvents} />
                    ) : (
                      <DataEmptyMessage title={filtersApplied ? "There are no community events found that match your filter criteria." : "You have no community events."} />
                    )}
                  </div>
                )}
                {currentTab === 'test' && (
                  <div
                    role="tabpanel"
                    id="test-events-panel"
                    aria-labelledby="test-events-tab"
                    aria-hidden={currentTab !== 'test' ? 'true' : 'false'}
                  >
                    {testEvents.length ? (
                      <EventsTable events={testEvents} />
                    ) : (
                      <DataEmptyMessage title={filtersApplied ? "There are no test events found that match your filter criteria." : "You have no test events."} />
                    )}
                  </div>
                )}
              </Grid>
            )}

            {!isError && events && !events.length && (
              <DataEmptyMessage title={filtersApplied ? "There are no events found that match your filter criteria." : "You have no events."} />
            )}
          </>
        )}

        {isError && (
            <DataLoadingError title={"Failed to load events!"} errors={error.errors} />
        )}
      </Grid>
    </div>
  );
};

export default ViewEvents;
